import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
	Box,
	capitalize,
	Card,
	CardContent,
	Chip,
	FormControl,
	FormControlLabel,
	Grid,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Switch,
	Typography,
	IconButton,
	Tooltip,
	Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import {
	MyCalendarIcon,
	MyCheckIcon,
	MyCreditCardIcon,
	MyMoneyIcon,
	MyInfoIcon,
} from "../../Assets/svg/MyIcons";
import CustomPlanPopup from "./CustomPlanPopup";
import { listAllPlansDropDownAction } from "../../Actions/PlanManagementActions";
import { getAllTenantAllowedFeaturesAction } from "../../Actions/TenantActions";
import { stepperLabelSx, stepperTextFieldSx } from "./stepperFormStyles";

// ============================================================================
// CONSTANTS
// ============================================================================

const FEATURE_LIMIT_MAP = {
	"User Limit": "userLimit",
	"Message Limit": "messageLimit",
	"Agent Training Limit": "agentLimit",
};

const SUB_FEATURES = {
	ai_chat: "user_chat",
	web_mail_tickets: "support_tickets",
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const calculatePrice = (exchangeRate, cycleAmount) => {
	return Number(Number(exchangeRate || 0) * Number(cycleAmount || 0)).toFixed(
		2
	);
};

const extractPlanLimits = (planFeatures) => {
	if (!planFeatures) return {};
	return planFeatures.reduce((acc, item) => {
		if (!item?.feature) return acc;
		const normalizedKey = FEATURE_LIMIT_MAP[item.feature.trim()];
		if (normalizedKey) {
			acc[normalizedKey] =
				typeof item.value == "string" ? null : Number(item.value);
		}
		return acc;
	}, {});
};

const initializeFeatureValues = (features, existingValues) => {
	const slugBasedFeatures = {};
	features.forEach((feature) => {
		if (feature.slug) {
			slugBasedFeatures[feature.slug] =
				existingValues[feature.slug] !== undefined
					? existingValues[feature.slug]
					: false;
		}
	});
	return slugBasedFeatures;
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const FeatureToggle = ({ feature, checked, onChange, onDisable }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			pl: 3,
			pr: 1.5,
			py: 1.5,
			borderRadius: 1,
			bgcolor: "white",
			border: "1px solid rgba(0, 0, 0, 0.12)",
			"&:hover": {
				bgcolor: "rgba(25, 118, 210, 0.02)",
			},
		}}
	>
		<Box sx={{ flex: 1 }}>
			<Typography variant="body1" sx={{ fontWeight: 600 }}>
				{feature.module}
			</Typography>
		</Box>
		<FormControlLabel
			control={
				<Switch
					checked={checked}
					onChange={(e) =>
						onChange(feature, e.target.checked, onDisable)
					}
					name={`allowedFeatures.${feature.slug}`}
					color="primary"
				/>
			}
			label=""
		/>
	</Box>
);

const SubFeatureToggle = ({ feature, checked, onChange }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			pl: 3,
			pr: 1.5,
			py: 1.5,
			ml: 2,
			borderRadius: 1,
			bgcolor: "white",
			border: "1px solid rgba(0, 0, 0, 0.12)",
			"&:hover": {
				bgcolor: "rgba(25, 118, 210, 0.02)",
			},
		}}
	>
		<Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
			<Typography
				variant="body1"
				sx={{
					fontWeight: 600,
					color: "text.primary",
				}}
			>
				{feature.module}
			</Typography>
		</Box>
		<FormControlLabel
			control={
				<Switch
					checked={checked}
					onChange={(e) => onChange(feature.slug, e.target.checked)}
					name={`allowedFeatures.${feature.slug}`}
					color="primary"
				/>
			}
			label=""
		/>
	</Box>
);

const StatCard = ({ icon: Icon, title, value, bgColor, iconBgColor }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			p: 2.5,
			borderRadius: 3,
			bgcolor: bgColor,
		}}
	>
		<Box
			sx={{
				width: 50,
				height: 50,
				background: iconBgColor,
				borderRadius: 2,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				mr: 2,
			}}
		>
			<Icon size={22} />
		</Box>
		<Box>
			<Typography
				variant="caption"
				sx={{
					color: "text.secondary",
					display: "block",
				}}
			>
				{title}
			</Typography>
			<Typography variant="body1" sx={{ fontWeight: 600 }}>
				{value}
			</Typography>
		</Box>
	</Box>
);

const PlanComparisonCard = ({
	planDetails,
	planMapPrice,
	currencyDetails,
	billingCycle,
	title,
	isUpcoming = false
}) => {
	const planData = planMapPrice[planDetails?.id] || {};
	const cycleMap = {
		monthly: planData?.monthly,
		yearly: planData?.yearly,
	};

	const price = billingCycle ? calculatePrice(
		currencyDetails.exchangeRate,
		cycleMap[billingCycle]
	) : "0.00";

	return (
		<Card
			sx={{
				height: "100%",
				border: isUpcoming ? "2px solid #22419e" : "1px solid rgba(0, 0, 0, 0.12)",
				position: "relative",
				overflow: "visible",
			}}
		>
			{isUpcoming && (
				<Box
					sx={{
						position: "absolute",
						top: -10,
						left: "50%",
						transform: "translateX(-50%)",
						bgcolor: "#22419e",
						color: "white",
						px: 2,
						py: 0.5,
						borderRadius: 1,
						fontSize: "0.75rem",
						fontWeight: 600,
						zIndex: 1,
					}}
				>
					UPCOMING
				</Box>
			)}
			<CardContent sx={{ p: 2.5 }}>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 700,
						mb: 1,
						textAlign: "center",
						color: isUpcoming ? "#22419e" : "text.primary",
					}}
				>
					{title}
				</Typography>
				<Typography
					variant="subtitle1"
					sx={{
						fontWeight: 600,
						mb: 2,
						textAlign: "center",
						color: "text.secondary",
					}}
				>
					{planData?.name || "No Plan"}
				</Typography>
				<Box sx={{ textAlign: "center", mb: 2 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: 700,
							color: isUpcoming ? "#22419e" : "primary.main",
						}}
					>
						{currencyDetails?.symbol} {price}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						per {billingCycle || "cycle"}
					</Typography>
				</Box>
				{planData?.features && (
					<Box>
						<Typography
							variant="subtitle2"
							sx={{ fontWeight: 600, mb: 1 }}
						>
							Features:
						</Typography>
						{planData.features.slice(0, 3).map((feature, index) => (
							<Typography
								key={index}
								variant="body2"
								sx={{
									display: "flex",
									alignItems: "center",
									mb: 0.5,
									fontSize: "0.8rem",
								}}
							>
								<MyCheckIcon
									size={14}
									style={{ marginRight: 8, color: "#4caf50" }}
								/>
								{feature.feature}: {feature.value}
							</Typography>
						))}
						{planData.features.length > 3 && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ fontStyle: "italic" }}
							>
								+{planData.features.length - 3} more features
							</Typography>
						)}
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TenantPlanStructureStep = ({
	formik,
	planMapPrice,
	setPlanMapPrice,
	selectedCurrency,
	tenantCurrencies,
	tenantId,
	merchantDetails,
}) => {
	const [planList, setPlanList] = useState([]);
	const [allowedFeatures, setAllowedFeatures] = useState([]);
	const [customPopupOpen, setCustomPopupOpen] = useState(false);
	const [isCustomPlan, setIsCustomPlan] = useState(
		formik.values.isCustomPlan || false
	);
	const [customPlan, setCustomPlan] = useState(() => {
		if (formik.values.isCustomPlan && formik.values.tenantCustomPlan) {
			return formik.values.tenantCustomPlan;
		}
		return formik.values.customPlanDetails || null;
	});
	// Tracks whether the user explicitly reset custom plan (toggled No), so we
	// don't restore tenantCustomPlan when they toggle back to Yes
	const customPlanUserReset = React.useRef(false);

	// State for plan comparison
	const [originalPlanId, setOriginalPlanId] = useState(
		tenantId && merchantDetails?.planId ? merchantDetails.planId : null
	);
	const [showPlanComparison, setShowPlanComparison] = useState(false);

	// ========================================================================
	// DERIVED VALUES & MEMOIZATION
	// ========================================================================
	const currencyDetails = useMemo(
		() =>
			tenantCurrencies.find((c) => c.id === Number(selectedCurrency)) || {
				exchangeRate: "1.0000",
				symbol: "$",
			},
		[tenantCurrencies, selectedCurrency]
	);

	const currentPlanDetails = useMemo(
		() => planMapPrice[formik.values.planId] || {},
		[planMapPrice, formik.values.planId]
	);

	const cycleMap = useMemo(
		() => ({
			monthly: currentPlanDetails?.monthly,
			yearly: currentPlanDetails?.yearly,
		}),
		[currentPlanDetails]
	);

	const extractedLimits = useMemo(
		() => extractPlanLimits(currentPlanDetails?.features),
		[currentPlanDetails?.features]
	);

	const isDowngradeRequested = formik.values.isDowngradeRequested || false;
	const tenantCustomPlan = formik.values.tenantCustomPlan || null;

	const initialLimits = useMemo(() => {
		const cd = formik.values.customPlanDetails;
		return {
			userLimit: cd && "userLimit" in cd ? cd.userLimit : extractedLimits?.userLimit ?? null,
			messageLimit: cd && "messageLimit" in cd ? cd.messageLimit : extractedLimits?.messageLimit ?? null,
			agentLimit: cd && "agentLimit" in cd ? cd.agentLimit : extractedLimits?.agentLimit ?? null,
		};
	}, [formik.values.customPlanDetails, extractedLimits]);

	const hasFeatureError = useMemo(() => {
		const allowedFeaturesValues = formik.values.allowedFeatures || {};
		const hasAtLeastOneFeature = allowedFeaturesValues.user_chat === true || allowedFeaturesValues.support_tickets === true;
		const hasError = formik.errors.allowedFeatures;
		return !hasAtLeastOneFeature && hasError
			? "error.main"
			: "rgba(25, 118, 210, 0.12)";
	}, [formik.values.allowedFeatures, formik.errors.allowedFeatures]);

	// ========================================================================
	// DATA FETCHING
	// ========================================================================
	const fetchAllPlansDropDownList = useCallback(async () => {
		try {
			const response = await listAllPlansDropDownAction();
			const { data = null } = response?.data || {};
			if (data) {
				const { plans, plansMap } = data;
				setPlanList(plans || []);
				setPlanMapPrice(plansMap || {});
			}
		} catch (error) {
			console.error("error ->", error);
		}
	}, [setPlanMapPrice]);

	const fetchAllowedFeatures = useCallback(async () => {
		try {
			const response = await getAllTenantAllowedFeaturesAction();
			if (response?.data?.status === 200 && response?.data?.data) {
				const features = response.data.data || [];
				setAllowedFeatures(features);

				const existingFeatures = formik.values.allowedFeatures || {};
				const slugBasedFeatures = initializeFeatureValues(
					features,
					existingFeatures
				);
				formik.setFieldValue("allowedFeatures", slugBasedFeatures);
			}
		} catch (error) {
			console.error("error fetching allowed features ->", error);
		}
	}, [formik]);

	useEffect(() => {
		fetchAllPlansDropDownList();
		fetchAllowedFeatures();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Initialize original plan ID for comparison (only for existing tenants)
	useEffect(() => {
		if (tenantId && merchantDetails?.planId && !originalPlanId) {
			setOriginalPlanId(merchantDetails.planId);
		}
	}, [tenantId, merchantDetails?.planId, originalPlanId, formik.values.planId]);

	// Auto-show plan comparison when formik plan differs from database plan
	useEffect(() => {
		if (tenantId && merchantDetails?.planId && formik.values.planId && originalPlanId) {
			const merchantPlanIdStr = String(merchantDetails.planId);
			const formikPlanIdStr = String(formik.values.planId);
			const shouldShowComparison = merchantPlanIdStr !== formikPlanIdStr;

			setShowPlanComparison(shouldShowComparison);
		}
	}, [tenantId, merchantDetails?.planId, formik.values.planId, originalPlanId, showPlanComparison]);

	// ========================================================================
	// EFFECTS
	// ========================================================================
	useEffect(() => {
		if (isCustomPlan && tenantCustomPlan && !customPlan && !customPlanUserReset.current) {
			// Only restore tenantCustomPlan on initial load, not after user explicitly reset
			setCustomPlan(tenantCustomPlan);
			formik.setFieldValue("customPlanDetails", tenantCustomPlan);
		} else if (
			isCustomPlan &&
			!customPlan &&
			extractedLimits &&
			formik.values?.billingCycle
		) {
			const initialPrice = calculatePrice(
				currencyDetails.exchangeRate,
				cycleMap[formik.values.billingCycle]
			);
			const initialCustomPlan = {
				userLimit: extractedLimits?.userLimit ?? null,
				messageLimit: extractedLimits?.messageLimit ?? null,
				agentLimit: extractedLimits?.agentLimit ?? null,
				price: Number(initialPrice),
			};
			setCustomPlan(initialCustomPlan);
			formik.setFieldValue("customPlanDetails", initialCustomPlan);
			// Clear the reset flag now that we've re-initialized from plan limits
			customPlanUserReset.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isCustomPlan,
		tenantCustomPlan,
		extractedLimits,
		formik.values?.billingCycle,
		currencyDetails.exchangeRate,
		cycleMap,
	]);

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================
	const handleFeatureToggle = useCallback(
		(feature, newValue, onDisable) => {
			const currentFeatures = formik.values.allowedFeatures || {};
			let updatedFeatures = {
				...currentFeatures,
				[feature.slug]: newValue,
			};

			// Auto-disable child features when parent is disabled
			if (feature.slug === "user_chat" && !newValue) {
				updatedFeatures.ai_chat = false;
			} else if (feature.slug === "support_tickets" && !newValue) {
				updatedFeatures.web_mail_tickets = false;
			}

			formik.setFieldValue("allowedFeatures", updatedFeatures);

			// Clear error if at least one feature is now enabled
			if (updatedFeatures.user_chat || updatedFeatures.support_tickets) {
				formik.setFieldError("allowedFeatures", undefined);
			}
		},
		[formik]
	);

	const handleSubFeatureToggle = useCallback(
		(featureSlug, newValue) => {
			const currentFeatures = formik.values.allowedFeatures || {};
			formik.setFieldValue("allowedFeatures", {
				...currentFeatures,
				[featureSlug]: newValue,
			});
		},
		[formik]
	);

	const handlePlanChange = useCallback(
		(e) => {
			const newPlanId = e.target.value;

			// Convert to same type for comparison (both should be strings or numbers)
			const newPlanIdStr = String(newPlanId);
			const originalPlanIdStr = String(originalPlanId);

			// If editing existing tenant and plan is changing to a different plan from the original database value
			if (tenantId && originalPlanId && newPlanIdStr !== originalPlanIdStr) {
				Swal.fire({
					title: "Change Plan?",
					html: `
						<p class="mb-3">Are you sure you want to change the plan?</p>
						<div class="alert alert-info bg-info-subtle border border-info-subtle text-start p-3 mb-0">
							<strong>Note:</strong> This will be applicable from the next billing cycle.
						</div>
					`,
					icon: "question",
					showCancelButton: true,
					confirmButtonColor: "#22419e",
					cancelButtonColor: "#777777",
					confirmButtonText: "Yes, Change Plan",
					cancelButtonText: "Cancel",
				}).then((result) => {
					if (result.isConfirmed) {
						formik.handleChange(e);
						if (isCustomPlan) {
							setCustomPlan(null);
							formik.setFieldValue("customPlanDetails", null);
						}
						// Show plan comparison when plan changes from original
						setShowPlanComparison(true);
					}
				});
			} else {
				// For new tenant, no change, or reverting to original plan, proceed normally
				formik.handleChange(e);
				if (isCustomPlan) {
					setCustomPlan(null);
					formik.setFieldValue("customPlanDetails", null);
				}
				// Show comparison only if plan is different from original database value
				if (tenantId && originalPlanId && newPlanIdStr !== originalPlanIdStr) {
					setShowPlanComparison(true);
				} else {
					setShowPlanComparison(false);
				}
			}
		},
		[formik, isCustomPlan, tenantId, originalPlanId, merchantDetails?.planId]
	);

	const handleBillingCycleChange = useCallback(
		(e) => {
			formik.handleChange(e);
			if (isCustomPlan && customPlan) {
				const newPrice = calculatePrice(
					currencyDetails.exchangeRate,
					cycleMap[e.target.value]
				);
				const updatedCustomPlan = {
					...customPlan,
					price: Number(newPrice),
				};
				setCustomPlan(updatedCustomPlan);
				formik.setFieldValue("customPlanDetails", updatedCustomPlan);
			}
		},
		[
			formik,
			isCustomPlan,
			customPlan,
			currencyDetails.exchangeRate,
			cycleMap,
		]
	);

	const handlePaymentModeChange = useCallback(
		(e) => {
			const newPaymentMode = e.target.value;
			const currentPaymentMode = formik.values.paymentMode;

			const applyOfflineDefaults = () => {
				// Offline: force Subscription=Yes (disabled), Custom Plan=Yes, all limits unlimited
				formik.setFieldValue("isAutoPay", true);
				setIsCustomPlan(true);
				formik.setFieldValue("isCustomPlan", true);
				customPlanUserReset.current = false;
				const price = calculatePrice(
					currencyDetails.exchangeRate,
					cycleMap[formik.values.billingCycle]
				);
				const unlimitedPlan = {
					userLimit: null,
					messageLimit: null,
					agentLimit: null,
					price: Number(price) || 0,
				};
				setCustomPlan(unlimitedPlan);
				formik.setFieldValue("customPlanDetails", unlimitedPlan);
				if (formik.values.hideBillingDetails === undefined) {
					formik.setFieldValue("hideBillingDetails", false);
				}
			};

			const applyOnlineDefaults = () => {
				// Online: reset Subscription and Custom Plan
				formik.setFieldValue("isAutoPay", false);
				setIsCustomPlan(false);
				formik.setFieldValue("isCustomPlan", false);
				setCustomPlan(null);
				formik.setFieldValue("customPlanDetails", null);
				formik.setFieldValue("hideBillingDetails", false);
			};

			// If editing existing tenant and payment mode is changing, show confirmation
			if (tenantId && currentPaymentMode && currentPaymentMode !== newPaymentMode) {
				Swal.fire({
					title: "Change Payment Mode?",
					html: `
						<p class="mb-3">Are you sure you want to change the payment mode to <strong>${newPaymentMode}</strong>?</p>
						<div class="alert alert-info bg-info-subtle border border-info-subtle text-start p-3 mb-0">
							<strong>Note:</strong> This will be applicable from the next billing cycle.
						</div>
					`,
					icon: "question",
					showCancelButton: true,
					confirmButtonColor: "#22419e",
					cancelButtonColor: "#777777",
					confirmButtonText: "Yes, Change Payment Mode",
					cancelButtonText: "Cancel",
				}).then((result) => {
					if (result.isConfirmed) {
						formik.handleChange(e);
						if (newPaymentMode === "offline") {
							applyOfflineDefaults();
						} else {
							applyOnlineDefaults();
						}
					}
				});
			} else {
				formik.handleChange(e);
				if (newPaymentMode === "offline") {
					applyOfflineDefaults();
				} else {
					applyOnlineDefaults();
				}
			}
		},
		[formik, tenantId, currencyDetails.exchangeRate, cycleMap]
	);

	const handleAutopayChange = useCallback(
		(e) => {
			const newAutopayValue = e.target.value === "true";
			// const currentAutopayValue = formik.values.isAutoPay;

			// If editing existing tenant and enabling autopay, show confirmation
			if (tenantId && newAutopayValue) {
				Swal.fire({
					title: "Enable Autopay?",
					html: `
						<p class="mb-3">Are you sure you want to enable autopay?</p>
						<div class="alert alert-info bg-info-subtle border border-info-subtle text-start p-3 mb-0">
							<strong>Note:</strong> This will be applicable from the next billing cycle.
						</div>
					`,
					icon: "question",
					showCancelButton: true,
					confirmButtonColor: "#22419e",
					cancelButtonColor: "#777777",
					confirmButtonText: "Yes, Enable Autopay",
					cancelButtonText: "Cancel",
				}).then((result) => {
					if (result.isConfirmed) {
						formik.handleChange(e);
					}
				});
			} else {
				// For new tenant, disabling autopay, or no change, proceed normally
				formik.handleChange(e);
			}
		},
		[formik, tenantId]
	);

	const handleCustomPlanChange = useCallback(
		(e) => {
			const value = e.target.value === "true";
			setIsCustomPlan(value);
			formik.setFieldValue("isCustomPlan", value);
			if (!value) {
				// Mark as user-reset so the useEffect won't restore tenantCustomPlan
				customPlanUserReset.current = true;
				setCustomPlan(null);
				formik.setFieldValue("customPlanDetails", null);
			}
		},
		[formik]
	);

	const handleCustomPlanSave = useCallback(
		(values) => {
			setCustomPlan(values);
			setCustomPopupOpen(false);
			formik.setFieldValue("customPlanDetails", values);
		},
		[formik]
	);


	const handleBillingDetailsVisibilityChange = useCallback(
		(e) => {
			const newValue = e.target.checked;
			const currentValue = formik.values.hideBillingDetails;

			if (tenantId && currentValue !== newValue) {
				const action = newValue ? "hide" : "show";
				const actionCapitalized = newValue ? "Hide" : "Show";

				Swal.fire({
					title: `${actionCapitalized} Billing Details?`,
					html: `
						<p class="mb-3">Are you sure you want to ${action} billing details from this tenant?</p>
						<div class="alert ${newValue ? 'alert-warning bg-warning-subtle border-warning-subtle' : 'alert-info bg-info-subtle border-info-subtle'} text-start p-3 mb-0">
							<strong>Impact:</strong>
							<p class="mb-0">
								${newValue
							? 'The tenant will <strong>NOT</strong> be able to view their billing history, payment records, and transaction details.'
							: 'The tenant will be able to view their billing history, payment records, and transaction details.'
						}
							</p>
						</div>
					`,
					icon: newValue ? "warning" : "question",
					showCancelButton: true,
					confirmButtonColor: "#22419e",
					cancelButtonColor: "#777777",
					confirmButtonText: `Yes, ${actionCapitalized}`,
					cancelButtonText: "Cancel",
				}).then((result) => {
					if (result.isConfirmed) {
						formik.setFieldValue("hideBillingDetails", newValue);
					}
				});
			} else {
				formik.setFieldValue("hideBillingDetails", newValue);
			}
		},
		[formik, tenantId]
	);

	// ========================================================================
	// RENDER HELPERS
	// ========================================================================
	const getBillingCyclePrice = useCallback(
		(cycle) => {
			return calculatePrice(
				currencyDetails.exchangeRate,
				cycleMap[cycle]
			);
		},
		[currencyDetails.exchangeRate, cycleMap]
	);

	const getBillingCycleLabel = useCallback(
		(cycle) => {
			const price = getBillingCyclePrice(cycle);
			const label = cycle === "monthly" ? "Monthly" : "Yearly";
			return `${label} - (${currencyDetails?.symbol} ${price})`;
		},
		[currencyDetails?.symbol, getBillingCyclePrice]
	);

	const calculateTotalPrice = useCallback(() => {
		const base = formik.values?.billingCycle
			? Number(getBillingCyclePrice(formik.values.billingCycle))
			: 0;

		const priceToUse =
			customPlan &&
				isCustomPlan &&
				customPlan.price != null &&
				!isNaN(Number(customPlan.price))
				? Number(customPlan.price)
				: base;

		return formik.values?.billingCycle
			? `${currencyDetails?.symbol} ${priceToUse.toFixed(2)}`
			: "-";
	}, [
		formik.values?.billingCycle,
		customPlan,
		isCustomPlan,
		currencyDetails?.symbol,
		getBillingCyclePrice,
	]);

	const getFeatureValue = useCallback(
		(item) => {
			let value = item?.value;
			if (customPlan && isCustomPlan && item?.feature) {
				const key = FEATURE_LIMIT_MAP[item.feature.trim()];
				if (key && customPlan[key] !== undefined) {
					value = customPlan[key] === null ? "Unlimited" : customPlan[key];
				}
			}
			// Capitalize "unlimited" if it comes from backend as lowercase
			if (typeof value === "string" && value.toLowerCase() === "unlimited") {
				return "Unlimited";
			}
			return value;
		},
		[customPlan, isCustomPlan]
	);

	const renderFeatureItem = useCallback(
		(feature) => {
			// Check if this feature has a parent requirement
			const parentSlug = SUB_FEATURES[feature.slug];
			const parentEnabled = parentSlug
				? formik.values.allowedFeatures?.[parentSlug]
				: true;

			// Hide child features when parent is disabled
			if (parentSlug && !parentEnabled) {
				return null;
			}

			// Check if this is a child feature
			const isChildFeature = !!parentSlug;

			return (
				<Grid
					item
					xs={12}
					key={`feature-${feature.slug}-${feature.id}`}
				>
					{isChildFeature ? (
						<Box sx={{ ml: 3 }}>
							<SubFeatureToggle
								feature={feature}
								checked={
									formik.values.allowedFeatures?.[
									feature.slug
									] || false
								}
								onChange={handleSubFeatureToggle}
							/>
						</Box>
					) : (
						<FeatureToggle
							feature={feature}
							checked={
								formik.values.allowedFeatures?.[feature.slug] ||
								false
							}
							onChange={handleFeatureToggle}
						/>
					)}
				</Grid>
			);
		},
		[
			formik.values.allowedFeatures,
			handleFeatureToggle,
			handleSubFeatureToggle,
		]
	);

	// ========================================================================
	// RENDER
	// ========================================================================
	return (
		<Grid container spacing={2}>
			{/* Left Column: Form */}
			<Grid item xs={12} md={7}>
				{/* Plan Selection */}
				<FormControl fullWidth sx={{ ...stepperTextFieldSx, mb: 3 }}>
					<Typography sx={stepperLabelSx}> Select Plan <span className="text-danger ms-1">*</span></Typography>
					<Select
						name="planId"
						value={formik.values.planId || ""}
						onChange={handlePlanChange}
						displayEmpty
						renderValue={(selected) => {
							if (!selected)
								return <em>Select a plan</em>;
							const plan = planList.find(
								(p) => p.id === selected
							);
							return plan ? plan.name : "";
						}}
						disabled={Boolean(planList.length === 0)}
					>
						<MenuItem value="" disabled>
							<em>Select a plan</em>
						</MenuItem>
						{planList.map((plan) => (
							<MenuItem
								key={`plan-${plan.id}`}
								value={plan.id}
							>
								{plan.name}
							</MenuItem>
						))}
					</Select>
					{formik.touched.planId && formik.errors.planId && (
						<Typography variant="caption" color="error">
							{formik.errors.planId}
						</Typography>
					)}
				</FormControl>

				{/* Billing Cycle */}
				<FormControl fullWidth sx={{ ...stepperTextFieldSx, mb: 3 }}>
					<Typography sx={stepperLabelSx}> Billing Cycle<span className="text-danger ms-1">*</span></Typography>
					<Select
						name="billingCycle"
						value={formik.values.billingCycle || ""}
						onChange={handleBillingCycleChange}
						displayEmpty
						disabled={Boolean(!formik.values.planId)}
						renderValue={(selected) => {
							if (!selected)
								return <em>Select billing cycle</em>;
							return getBillingCycleLabel(selected);
						}}
					>
						<MenuItem value="" disabled>
							<em>Select billing cycle</em>
						</MenuItem>
						<MenuItem value="monthly">
							Monthly - ({currencyDetails?.symbol}{" "}
							{getBillingCyclePrice("monthly")})
						</MenuItem>
						<MenuItem value="yearly">
							Yearly - ({currencyDetails?.symbol}{" "}
							{getBillingCyclePrice("yearly")})
						</MenuItem>
					</Select>
					{formik.touched.billingCycle &&
						formik.errors.billingCycle && (
							<Typography variant="caption" color="error">
								{formik.errors.billingCycle}
							</Typography>
						)}
				</FormControl>

				{/* Payment Mode and AutoPay in Grid */}
				<Grid container spacing={2} sx={{ mb: 3 }}>
					{/* {!tenantId && ( */}
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
								}}
							>
								<Typography variant="subtitle2" gutterBottom>Payment Mode<span className="text-danger ms-1">*</span></Typography>
								{tenantId && <Tooltip
									title={
										<Typography variant="body2">
											Payment mode can be applicable on the next plan payment cycle
										</Typography>
									}
									arrow
									placement="right-start"
								>
									<IconButton size="small">
										<MyInfoIcon size={18} />
									</IconButton>
								</Tooltip>}
							</Box>
							<RadioGroup
								row
								name="paymentMode"
								value={
									formik.values.paymentMode || ""
								}
								onChange={handlePaymentModeChange}
							>
								<FormControlLabel
									value="online"
									control={<Radio />}
									label="Online"
								/>
								<FormControlLabel
									value="offline"
									control={<Radio />}
									label="Offline"
								/>
							</RadioGroup>
							{formik.touched.paymentMode &&
								formik.errors.paymentMode && (
									<Typography
										variant="caption"
										color="error"
									>
										{formik.errors.paymentMode}
									</Typography>
								)}
						</FormControl>
					</Grid>
					{/* )} */}
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth>
							<Typography variant="subtitle2" gutterBottom >Subscription<span className="text-danger ms-1">*</span></Typography>
							<RadioGroup
								row
								name="isAutoPay"
								value={formik.values.isAutoPay}
								onChange={handleAutopayChange}
							>
								<FormControlLabel
									value={true}
									control={<Radio />}
									label="Yes"
									disabled={formik.values.paymentMode === "offline"}
								/>
								<FormControlLabel
									value={false}
									control={<Radio />}
									label="No"
									disabled={formik.values.paymentMode === "offline"}
								/>
							</RadioGroup>
							{formik.touched.isAutoPay &&
								formik.errors.isAutoPay && (
									<Typography
										variant="caption"
										color="error"
									>
										{formik.errors.isAutoPay}
									</Typography>
								)}
						</FormControl>
					</Grid>
				</Grid>

				<Grid container spacing={2} sx={{ mb: 3 }}>
					{/* Custom Plan */}
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
								}}
							>
								<Typography variant="subtitle2"> Custom Plan <span className="text-danger ms-1">*</span></Typography>
								<Tooltip
									title={
										<Typography variant="body2">
											{isDowngradeRequested
												? "Tenant has a active downgrade request. Custom plan changes are disabled until the downgrade is cancelled."
												: "Custom plan with special features"}
										</Typography>
									}
									arrow
									placement="right-start"
								>
									<IconButton size="small">
										<MyInfoIcon size={18} />
									</IconButton>
								</Tooltip>
							</Box>
							<RadioGroup
								row
								value={isCustomPlan}
								onChange={handleCustomPlanChange}
							>
								<FormControlLabel
									value={true}
									control={<Radio />}
									label="Yes"
									disabled={Boolean(isDowngradeRequested)}
								/>
								<FormControlLabel
									value={false}
									control={<Radio />}
									label="No"
									disabled={Boolean(isDowngradeRequested)}
								/>
							</RadioGroup>
						</FormControl>
					</Grid>

					{/* Transaction Details Checkbox - Only show when offline payment mode is selected */}
					{formik.values.paymentMode === "offline" && (
						<Grid item xs={12} sm={6}>
							<FormControl fullWidth>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										mb: 1,
									}}
								>
									<Typography variant="subtitle2">
										Billing Details Visibility
									</Typography>
									<Tooltip
										title={
											<Typography variant="body2">
												When enabled, billing details will be hidden from the tenant. They will not be able to view their billing history and payment records.
											</Typography>
										}
										arrow
										placement="right-start"
									>
										<IconButton size="small">
											<MyInfoIcon size={18} />
										</IconButton>
									</Tooltip>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center" }}>
									<Checkbox
										checked={formik.values.hideBillingDetails || false}
										onChange={handleBillingDetailsVisibilityChange}
										name="hideBillingDetails"
										color="primary"
									/>
									<Typography variant="body2">
										Hide billing details from tenant
									</Typography>
								</Box>
							</FormControl>
						</Grid>
					)}
				</Grid>
				{/* Allowed Features */}
				{allowedFeatures.length > 0 && (
					<Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mb: 1,
							}}
						>
							<Typography variant="subtitle2"> Allowed Features <span className="text-danger ms-1">*</span></Typography>
							<Tooltip
								title={
									<Typography variant="body2">
										Select the features you want to
										enable for this tenant. User
										Chat and Support Tickets are
										available. At least one must be
										enabled.
									</Typography>
								}
								arrow
								placement="right-start"
							>
								<IconButton size="small">
									<MyInfoIcon size={18} />
								</IconButton>
							</Tooltip>
						</Box>
						<Box
							sx={{
								p: 2,
								borderRadius: 2,
								bgcolor: "rgba(25, 118, 210, 0.04)",
								border: "1px solid",
								borderColor: hasFeatureError,
							}}
						>
							<Grid container spacing={2}>
								{allowedFeatures.map(renderFeatureItem)}
							</Grid>
						</Box>
					</Box>
				)}
			</Grid>

			{/* Right Column: Plan Preview */}
			<Grid item xs={12} md={5}>
				{/* Plan Header */}
				<Box
					sx={{
						bgcolor: "grey.50",
						p: 2.5,
						borderRadius: 1,
						mb: 3,
						boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
					}}
				>
					<Box sx={{ textAlign: "center", mb: 3 }}>
						<Box
							sx={{
								width: 90,
								height: 90,
								background: "linear-gradient(135deg, #1976d2, #3f51b5)",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "#fff",
								mx: "auto",
								mb: 2,
							}}
						>
							<MyCreditCardIcon size={36} className="text-white" />
						</Box>
					</Box>
					{/* Payment Mode Tag */}
					{formik.values?.paymentMode && (
						<Box sx={{ mb: 2, textAlign: "center" }}>
							<Chip
								label={
									formik.values.paymentMode === "online" ? "Online" : "Offline"
								}
								sx={{
									px: 3,
									py: 1,
									fontWeight: 600,
									fontSize: 14,
									color: "#fff",
									borderRadius: "50px",
									bgcolor: formik.values.paymentMode === "online" ? "primary.main" : "grey.500",
									background: formik.values.paymentMode === "online" ? "linear-gradient(135deg, #22419e, #22419e)" : "linear-gradient(135deg, #b0bec5, #78909c)",
									boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
								}}
							/>
						</Box>
					)}

					<Box sx={{ textAlign: "center" }}>
						<Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								{currentPlanDetails?.name ?
									(customPlan && isCustomPlan ? (
										"Custom"
									) : currentPlanDetails?.name)
									: "No Plan Selected"
								}
								{isCustomPlan &&
									!isDowngradeRequested && (
										<IconButton
											size="small"
											onClick={() =>
												setCustomPopupOpen(true)
											}
											aria-label="Edit custom plan"
										>
											<EditIcon fontSize="small" />
										</IconButton>
									)}
							</Box>
						</Typography>

						{formik.values?.billingCycle && (
							<Box
								sx={{
									display: "flex",
									alignItems: "baseline",
									justifyContent: "center",
								}}
							>
								<Typography
									variant="h5"
									sx={{
										fontWeight: 700,
										color: "primary.main",
										mr: 0.5,
									}}
								>
									{customPlan &&
										isCustomPlan &&
										customPlan.price != null &&
										!isNaN(Number(customPlan.price)) ? (
										<span>
											{currencyDetails?.symbol}{" "}
											{Number(
												customPlan.price
											).toFixed(2)}
										</span>
									) : (
										calculateTotalPrice()
									)}
								</Typography>
							</Box>
						)}
					</Box>
				</Box>

				{/* Billing Summary Box */}
				<Box
					sx={{
						bgcolor: "grey.50",
						p: 2.5,
						borderRadius: 1,
						mb: 3,
						boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
					}}
				>
					<Typography
						variant="subtitle1"
						fontWeight={600}
						color="var(--bs-primary)"
						sx={{ mb: 1.5 }}
					>
						Billing Summary
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<StatCard
								icon={MyCalendarIcon}
								title="Billing Cycle"
								value={
									formik.values?.billingCycle
										? capitalize(
											formik.values
												.billingCycle
										)
										: "-"
								}
								bgColor="rgba(25, 118, 210, 0.08)"
								iconBgColor="linear-gradient(135deg, #bbdefb, #90caf9)"
							/>
						</Grid>
						<Grid item xs={12}>
							<StatCard
								icon={MyMoneyIcon}
								title="Total Amount"
								value={calculateTotalPrice()}
								bgColor="rgba(76, 175, 80, 0.08)"
								iconBgColor="linear-gradient(135deg, #c8e6c9, #a5d6a7)"
							/>
						</Grid>
					</Grid>
				</Box>

				{/* Features Box */}
				{currentPlanDetails?.features && (
					<Box
						sx={{
							bgcolor: "grey.50",
							p: 2.5,
							borderRadius: 1,
							mb: 3,
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
						}}
					>
						<Typography
							variant="subtitle1"
							fontWeight={600}
							color="var(--bs-primary)"
							sx={{ mb: 1.5 }}
						>
							What's Included
						</Typography>
						{formik.values?.billingCycle === "yearly" && (
							<Box sx={{ mb: 2 }}>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									ℹ️ Your usage limits refresh every
									month as part of the yearly plan.
								</Typography>
							</Box>
						)}
						<Grid container spacing={1}>
							{(currentPlanDetails?.features || []).map(
								(item, index) => {
									const value = getFeatureValue(item);
									return (
										<Grid
											item
											xs={12}
											key={`plan-feature-${index}-${item?.feature || index
												}`}
										>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
												}}
											>
												<Box
													sx={{
														width: 26,
														height: 26,
														background: "linear-gradient(135deg, #4caf50, #66bb6a)",
														borderRadius: "50%",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														color: "white",
														mr: 2,
														flexShrink: 0,
													}}
												>
													<MyCheckIcon size={14} />
												</Box>
												<Typography variant="body2">
													{item?.feature || ""}{" "} - {value || ""}
												</Typography>
											</Box>
										</Grid>
									);
								}
							)}
						</Grid>
					</Box>
				)}

				{/* Plan Comparison Section */}
				{(() => {
					const shouldShow = showPlanComparison && tenantId && originalPlanId && formik.values.planId && originalPlanId !== formik.values.planId && formik.values.paymentMode !== "offline";
					return shouldShow;
				})() && (
						<Box
							sx={{
								bgcolor: "grey.50",
								p: 2.5,
								borderRadius: 1,
								mb: 3,
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="var(--bs-primary)"
								sx={{ mb: 2 }}
							>
								Plan Comparison
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 2, textAlign: "left" }}
							>
								Compare your current plan with the upcoming plan
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<PlanComparisonCard
										planDetails={{ id: originalPlanId }}
										planMapPrice={planMapPrice}
										currencyDetails={currencyDetails}
										billingCycle={formik.values.billingCycle}
										title="Current Plan"
										isUpcoming={false}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<PlanComparisonCard
										planDetails={{ id: formik.values.planId }}
										planMapPrice={planMapPrice}
										currencyDetails={currencyDetails}
										billingCycle={formik.values.billingCycle}
										title="Upcoming Plan"
										isUpcoming={true}
									/>
								</Grid>
							</Grid>
							<Box sx={{ mt: 2, textAlign: "left" }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										display: "inline-flex",
										alignItems: "left",
										bgcolor: "rgba(34, 65, 158, 0.1)",
										px: 2,
										py: 1,
										borderRadius: 1,
									}}
								>
									<MyInfoIcon size={16} style={{ marginRight: 8 }} />
									The upcoming plan will be effective from the next billing cycle
								</Typography>
							</Box>
						</Box>
					)}

				<CustomPlanPopup
					open={customPopupOpen}
					onClose={() => setCustomPopupOpen(false)}
					billingCycleLabel={
						formik.values?.billingCycle || "Billing Cycle"
					}
					currencySymbol={currencyDetails?.symbol}
					priceValue={
						customPlan?.price !== undefined &&
							customPlan?.price !== null
							? customPlan.price
							: calculatePrice(
								currencyDetails.exchangeRate,
								cycleMap[
								formik.values?.billingCycle
								]
							)
					}
					planDefaultPrice={calculatePrice(
						currencyDetails.exchangeRate,
						cycleMap[formik.values?.billingCycle]
					)}
					noPlanSelected={!formik.values?.planId}
					initialLimits={initialLimits}
					onSave={handleCustomPlanSave}
				/>
			</Grid>
		</Grid>
	);
};

export default TenantPlanStructureStep;
